import * as Yup from 'yup';
import { useProduct } from 'iomart-hooks';
import { ExtendFile } from 'src/components/file-thumbnail/types';
import { getProductsTest } from 'src/utils/validation';
import { deleteProductFilesById, uploadProductFilesById } from 'src/utils/directUpload';

type UseHook = typeof useProduct;
export type ProductItem = ReturnType<UseHook>['item'];

const uniqueOrdered = (items: string[]) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!item || seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
};

export const getProductDefaultValues = (item: ProductItem | null) => ({
  productId: item?.id || null,
  name: item?.name || '',
  description: item?.description || '',
  shortDescription: item?.shortDescription || '',
  rrc: item?.rrc || null,
  costPriceRub: item?.costPriceRub || null,
  oldPrice: item?.oldPrice || null,
  hsCode: (item as any)?.hsCode || '',
  width: item?.width || null,
  length: item?.length || null,
  height: item?.height || null,
  weight: item?.weight || null,
  volume: item?.volume || null,
  barcodes: item?.barcodes || [],
  articles: item?.articles || [],
  status: item?.status || 'drafts',
  images: item?.images || ([] as (string | ExtendFile | null)[]),
  brand: item?.brand,
  category: item?.category,
  products: item?.products ?? [],
});

export type ProductFormData = ReturnType<typeof getProductDefaultValues>;

export const productFormSchemaFields = {
  name: Yup.string().nullable().required('Name is required'),
  articles: Yup.array()
    .of(Yup.string())
    .min(1, 'Articles field must have at least 1 items')
    .required('At least one article is required'),
  products: getProductsTest(),
};

export const productFormSchema = Yup.object().shape(productFormSchemaFields);

export type ProductSubmitMeta = {
  id?: number | string;
  hook?: { updateItem?: (id: number, data: any) => Promise<any> };
};

export async function buildProductSubmitPayload(
  formData: ProductFormData,
  meta?: ProductSubmitMeta
): Promise<Record<string, any>> {
  const { productId: _omitProductId, ...cleanFormData } = formData as any;
  const {
    status,
    products: productData = [],
    images: formImages = [],
    costPriceRub,
    brand,
    category,
    ...data
  } = cleanFormData;

  const existingImages: string[] = (formImages as (string | File)[])
    .filter((img) => !!img)
    .map((img) => (typeof img === 'string' ? img : ''))
    .filter(Boolean);

  const newFiles: ExtendFile[] = (formImages as (string | File)[]).filter(
    (img): img is File => !!img && img instanceof File
  );

  const products = (productData as { product?: { id: number }; count: number }[]).map((p) => ({
    id: p.product?.id,
    count: p.count,
  })).filter((p) => p.id);

  const payloadBase = {
    ...data,
    productBrand_id: brand?.id,
    productCategory_id: category?.id,
    images: existingImages,
    products,
  };

  const articleForFolder = (data.articles || [])[0];
  if (!articleForFolder || !newFiles.length) {
    return payloadBase;
  }

  const patchImagesAfterSave = async ({
    id,
    hook,
  }: {
    id: number;
    result: any;
    hook: ProductSubmitMeta['hook'];
    hooks?: any[];
  }) => {
    if (!hook?.updateItem) return;
    const uploadedUrls = await uploadProductFilesById(id, newFiles);
    const fileToUploadedUrl = new Map<File, string>();
    newFiles.forEach((file, index) => {
      const url = uploadedUrls[index];
      if (url) fileToUploadedUrl.set(file, url);
    });
    const nextImages = (formImages as (string | File)[])
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return fileToUploadedUrl.get(item) || '';
      })
      .filter(Boolean) as string[];
    const mappedUploadUrls = new Set(fileToUploadedUrl.values());
    const missingUploaded = uploadedUrls.filter((url) => !mappedUploadUrls.has(url));
    const mergedImages = uniqueOrdered([...nextImages, ...missingUploaded]);
    try {
      await hook.updateItem(id, { ...payloadBase, images: mergedImages });
    } catch (err) {
      await deleteProductFilesById(id, uploadedUrls);
      throw err;
    }
  };

  if (meta?.id) {
    return { ...payloadBase, __afterUpdate: patchImagesAfterSave };
  }
  return { ...payloadBase, __afterCreate: patchImagesAfterSave };
}
